import { FillContentLoader } from '@shared/components/Loader';
import { useMutation, useQuery } from '@tanstack/react-query';
import { D3Captive } from './D3';
import { CaptivePortalData, CaptivePortalProps } from './common';
import Container from '@mui/material/Container';
import { Box, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaptivePortalRenderer } from '@shared/components/captive-portal/CaptivePortalRenderer';
import { CaptivePortalFormData } from '@shared/components/captive-portal';

export function useCaptivePortalData() {
  return useQuery<CaptivePortalData, Error>(
    ['captive-portal'],
    async () => {
      const res = await fetch('/api/captive-portal');
      if (!res.ok) {
        throw new Error(await res.text());
      }
      return res.json();
    },
    {
      onError: (err) => {
        console.error(`Failed to fetch captive portal data: ${err.message}`);
      },
      refetchInterval: 60000,
      staleTime: 60000,
      retry: 0,
    },
  );
}

export function CaptivePortalPage() {
  const query = useCaptivePortalData();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, onFormDataChange] = useState<CaptivePortalFormData>({});

  useEffect(() => {
    if (query.error) {
      navigate('/activate');
    }
  }, [!!query.error]);

  const loginMutation = useMutation(
    async (loginData: {
      firstName?: string;
      lastName?: string;
      email?: string;
      captivePortal: CaptivePortalData;
    }) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // TODO endpoint
    },
    {
      onError: () => {
        console.error('Login failed');
      },
    },
  );

  if (query.isLoading || !query.data) {
    return (
      <Container
        component="main"
        maxWidth="xl"
        sx={{
          m: 0,
          pb: 0,
          height: '100vh',
        }}
      >
        <FillContentLoader />
      </Container>
    );
  }

  if (loginMutation.isSuccess) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="80vh"
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: '100%',
              maxWidth: 500,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              {t('connectionSuccessful', 'Connection Successful')}
            </Typography>

            <Box display="flex" justifyContent="center" m={3}>
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4CAF50"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </Box>

            <Typography variant="body1">
              {t('connectedToInternet', 'You are now connected to the internet.')}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t('closeTab', 'You can close this tab and continue browsing.')}
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (query.data.captivePortalSettings?.template) {
    return (
      <CaptivePortalRenderer
        config={query.data.captivePortalSettings}
        onClickSubmit={() =>
          loginMutation.mutate({
            ...formData,
            captivePortal: query.data,
          })
        }
        fullScreen={true}
        formData={formData}
        onFormDataChange={onFormDataChange}
        isSubmitting={loginMutation.isLoading}
      />
    );
  }

  const props: CaptivePortalProps = {
    data: query.data,
    login: async (loginData) => {
      loginMutation.mutate(loginData);
    },
    isError: query.isError,
    isLoading: loginMutation.isLoading,
    isSuccess: loginMutation.isSuccess,
  };
  return <D3Captive {...props} />;
}
